import { useEffect, useState } from 'react';

import {
  EvaluationBatch,
  getEvaluationBatches,
  restartEvaluation as restartEvaluationService,
  startEvaluation as startEvaluationService,
  stopEvaluation as stopEvaluationService,
} from '../services/evaluationService';

export const useEvaluationBatches = (options?: { pausePolling?: boolean }) => {
  const [batches, setBatches] = useState<EvaluationBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pausePolling = options?.pausePolling ?? false;

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const data = await getEvaluationBatches();
      setBatches(data.batches || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching evaluation batches:', err);
      setError('Failed to load evaluation batches');
    } finally {
      setLoading(false);
    }
  };

  const startEvaluation = async (batchId: string) => {
    await startEvaluationService(batchId);
    await fetchBatches();
  };

  const stopEvaluation = async (batchId: string) => {
    await stopEvaluationService(batchId);
    await fetchBatches();
  };

  const restartEvaluation = async (batchId: string) => {
    await restartEvaluationService(batchId);
    await fetchBatches();
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  // Poll for updates if any batch is running, unless paused
  const hasRunningBatch = batches.some((b) => b.status === 'running');
  useEffect(() => {
    if (!hasRunningBatch || pausePolling) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchBatches();
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [hasRunningBatch, pausePolling]);

  return {
    batches,
    loading,
    error,
    refetch: fetchBatches,
    startEvaluation,
    stopEvaluation,
    restartEvaluation,
  };
};
